import React, { Fragment, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import Avatar from 'react-avatar';
import { Badges, Btn } from '../../../AbstractElements';
import { disableUserAPI, terminateUserAPI } from '../../../Library/apis';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../Library/custom_utils';
import { Spinner } from 'reactstrap';
import MothriftLoader from '../Common/MothriftLoader';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router';

const ActionButtons = ({ item, updateAllCustomers, isEditable }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTerminating, setIsTerminating] = useState(false);

    const handleDisable = () => {
        setIsDeleting(true);
        disableUserAPI({ userId: item.userInfo._id })
            .then(res => {
                toast.success(res.message, { autoClose: 1000 });
                updateAllCustomers();
            })
            .catch(error => toast.error(error?.response?.data?.message || 'Error'))
            .finally(() => setIsDeleting(false));
    };

    const handleTerminate = () => {
        Swal.fire({
            title: 'Terminate Term Savings?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, terminate',
        }).then(result => {
            if (result.isConfirmed) {
                setIsTerminating(true);
                terminateUserAPI({ userId: item.userInfo._id, amount: item.termSavingsWallet })
                    .then(res => {
                        toast.success(res.message);
                        updateAllCustomers();
                    })
                    .catch(error => toast.error(error?.response?.data?.message || 'Error'))
                    .finally(() => setIsTerminating(false));
            }
        });
    };

    if (!isEditable) {
        return (
            <Badges attrBadge={{ className: 'badge rounded-pill', color: item.userInfo.active ? 'primary' : 'secondary', pill: true }}>
                {item.userInfo.active ? 'enabled' : 'disabled'}
            </Badges>
        );
    }

    return (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Btn attrBtn={{ color: item.userInfo.active ? 'secondary' : 'info', size: 'xs', onClick: handleDisable, style: { borderRadius: 20 } }}>
                {isDeleting && <Spinner size='sm' className='me-1' />}
                {item.userInfo.active ? 'Disable' : 'Enable'}
            </Btn>
            {item.termSavingsWallet > 0 && (
                <Btn attrBtn={{ color: 'danger', size: 'xs', onClick: handleTerminate, style: { borderRadius: 20 } }}>
                    {isTerminating && <Spinner size='sm' className='me-1' />}
                    Terminate
                </Btn>
            )}
        </div>
    );
};

const CustomerTable = ({ data, updateAllCustomers, isEditable, isLoading, totalRows, currentPage, onPageChange, limit }) => {
    const navigate = useNavigate();

    const tableColumns = useMemo(() => [
        {
            name: 'User Name/ID',
            minWidth: '220px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                    <Avatar name={`${row.userInfo.firstName} ${row.userInfo.lastName}`} size='35' round src={row.userInfo.profileImageUrl} color='#7366FF' />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                        <span className='f-w-600 text-uppercase' style={{ fontSize: '12px' }}>{row.userInfo.firstName} {row.userInfo.lastName}</span>
                        <small className='text-muted'>{row.userInfo.userId}</small>
                    </div>
                </div>
            ),
        },
        { name: 'Savings', width: '120px', cell: row => formatCurrency(row.savingsWallet) },
        { name: 'Terms', width: '120px', cell: row => formatCurrency(row.termSavingsWallet) },
        { name: 'Interest', width: '120px', cell: row => formatCurrency(row.interestWallet) },
        { name: 'Phone', width: '140px', selector: row => row.userInfo.phoneNumber },
        {
            name: 'Status',
            width: '100px',
            cell: row => (
                <Badges attrBadge={{ className: 'badge rounded-pill', color: row.userInfo.confirmed ? 'info' : 'warning', pill: true }}>
                    {row.userInfo.confirmed ? 'Verified' : 'Pending'}
                </Badges>
            ),
        },
        {
            name: 'Actions',
            minWidth: '180px',
            cell: row => <ActionButtons item={row} updateAllCustomers={updateAllCustomers} isEditable={isEditable} />,
        },
        {
            name: 'Profile',
            center: true,
            minWidth: '150px',
            cell: row => (
                <button
                    onClick={() => navigate(`/mothrift/customer-management/customer-profile/${row.userInfo.userId}`)}
                    className="btn btn-outline-primary"
                    style={{ 
                        whiteSpace: 'nowrap', 
                        padding: '6px 15px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        borderRadius: '8px',
                        textTransform: 'uppercase'
                    }}
                >
                    Investor's Page
                </button>
            ),
        }
    ], [updateAllCustomers, isEditable, navigate]);

    return (
        <Fragment>
            {isLoading ? (
                <MothriftLoader />
            ) : (
                <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', background: '#fff' }}>
                    <DataTable
                        data={data}
                        columns={tableColumns}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={limit}
                        paginationDefaultPage={currentPage}
                        onChangePage={onPageChange}
                        responsive
                        highlightOnHover
                        pointerOnHover
                        persistTableHead
                        customStyles={{
                            table: {
                                style: {
                                    // Removed the 400px minHeight to prevent awkward gaps during searches
                                    minHeight: 'auto', 
                                },
                            },
                            rows: {
                                style: {
                                    minHeight: '70px',
                                },
                            },
                            headCells: {
                                style: {
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    backgroundColor: '#f8f9fa'
                                }
                            }
                        }}
                    />
                </div>
            )}
        </Fragment>
    );
};

export default CustomerTable;